import React from 'react';
import { Toast } from 'components/shared/Toaster'
import { getBackendUrl } from './url'
import Auth from './AuthService'
import history from './history'
import strings from 'strings'

const throwException = error => {
  if (error.status === 403) {
    history.push('/login')
  }

  if (!navigator.onLine) {
    Toast({ type: 'error', message: strings.LOGIN_STRING.CHECK_INTERNET_CONNECTION })
    return;
  }
  if (Array.isArray(error)) {
    const errMsg = error.reduce((accum, current) => accum + ' ' + (current?.message || ''), ' ').trim()
    Toast({ type: 'error', message: errMsg || strings.SOMETHING_WENT_WRONG })
    return
  }
  if(Array.isArray(error?.details)) {
    const errMsg = error.details.map(item => <p>{item}</p>)
    Toast({ type: 'error', message: <><h6>{error?.message}</h6> {errMsg}</>, autoClose: false })
  }
  else Toast({ type: 'error', message: error.message })
}

/**
 * Makes an HTTP request.
 *
 * @param {string} path - The URL path for the request.
 * @param {string} method - The HTTP method for the request (e.g. GET, POST, PUT, DELETE).
 * @param {object} body - The request body data.
 * @param {boolean} [withHeaders=false] - Whether to include response headers in the result.
 * @param {object} [headers={}] - Additional headers for the request.
 * @param {object} params - Query parameters for the request.
 * @param {boolean} [error404Redirect=true] - Whether a 404 redirects to the /404 page.
 * @param {boolean} [silentOnError=false] - Skip the automatic error Toast so the caller handles the
 *   failure itself. The response body is also left unread, so the caller can parse it from the
 *   rejected Response (a non-silent request consumes it to build the Toast).
 * @returns {Promise} A promise that resolves with the parsed response or rejects with an error.
 */
const request = (
  path, // The URL path for the request
  method, // The HTTP method for the request (e.g. GET, POST, PUT, DELETE)
  body, // The request body data
  withHeaders = false, // Whether to include response headers in the result
  headers = {}, // Additional headers for the request
  params, // Query parameters for the request
  error404Redirect = true,
  // Skip auto-Toast on error so callers can handle it (e.g. silent retries, best-effort load-time
  // fetches). The response body is also left unread so the caller can parse it.
  silentOnError = false
) => {
  return new Promise((resolve, reject) => {
    const responseHeaders = {}
    return baseRequest(path, method, body, headers, params, true)
      .then(response => {
        if (response.status >= 200 && response.status < 400) {
          return response
        }

        const error = new Error(response.statusText)
        error.response = response

        throw error
      })
      .then(response => {
        if (response.headers.entries) {
          for (var pair of response.headers.entries()) {
            responseHeaders[pair[0]] = pair[1]
          }
        }

        return response.status !== 204 && response.json()
      })
      .then(parsedResponse => {
        if (withHeaders) {
          resolve({
            response: parsedResponse,
            headers: responseHeaders
          })

          return
        }
        resolve(parsedResponse)
      })
      .catch(async err => {
        if (err.response) {
          // 401/403 already triggered logout+redirect in validateAuthResponse.
          // Skip the toast and the /404 redirect so parallel in-flight requests
          // during logout don't spam "[object Response]" toasts or race us to /404.
          const isAuthFailure = err.response.status === 401 || err.response.status === 403
          try {
            if (err.response.status !== 404 && !isAuthFailure) {
              // The json() stays inside the guard on purpose: reading it consumes the body, and silent
              // callers reject with the Response so they can parse the error themselves.
              if (!silentOnError) {
                try {
                  const parsedError = await err.response?.json()
                  throwException(parsedError)
                } catch (error) {
                  Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG})
                }
              }
            } else if (err.response.status === 404) {
              if (error404Redirect){
                history.push('/404')
             }
            }
            return reject(err.response)
          } catch (e) {
            if (!silentOnError && !isAuthFailure) Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG })
            // Will throw if the backend responed and the response has no data (responses like 404)
            return reject(e)
          }
        }
        // Network/timeout failure (no response received). Surface a friendly
        // "Network error" toast instead of the raw "TypeError: Failed to fetch".
        if (!silentOnError) Toast({ type: 'error', message: strings.LOGIN_STRING.CHECK_INTERNET_CONNECTION })
        reject()
      })
  })
}

export default request

let refreshPromise = null

/** * Ensures only a single token refresh happens at a time. * All concurrent callers await the same promise. */
const refreshTokenOnce = () => {
  if (!refreshPromise) {
    refreshPromise = Auth.refreshToken()
        .finally(() => {
          refreshPromise = null
        })
  }
  return refreshPromise
}

/**
 * if the response is 401/403 will refresh the token, if refresh token fails will redirect to login
 * if refresh token succeed, then get that token and save it as the new
 * Authorization token that will be used in future requests
 * After that finishes recall the same request that returned 401/403 at the beginning with the new Authorization token
 * If that failed again, then skip log the user out
 */
const validateAuthResponse = async (
    res,
    path,
    method,
    body,
    headers,
    params,
    acceptJson,
    skipRefresh
) => {
  if (res.status !== 401 && res.status !== 403) {
    return res
  }

  // Logout is already in progress from an earlier failing request or the user
  // clicking Log out — don't attempt a token refresh, don't stack redirects.
  // Just reject so the caller stops processing while the page reload finishes.
  if (Auth.isLoggingOut()) {
    return Promise.reject(wrapAuthResponse(res))
  }

  // Already retried after refresh — give up
  if (skipRefresh) {
    Auth.logout()
    return Promise.reject(wrapAuthResponse(res))
  }

  try {
    await refreshTokenOnce()
  } catch (e) {
    Auth.logout()
    return Promise.reject(wrapAuthResponse(res))
  }

  // Retry the original request with the new token (skipRefresh=true to avoid infinite loop)
  return baseRequest(path, method, body, headers, params, acceptJson, true)
}

// Wrap a raw Response in a proper Error so the .catch in request() can inspect
// err.response.status and route through the normal error path (rather than
// stringifying the Response into "[object Response]" toasts).
const wrapAuthResponse = (res) => {
  const error = new Error(res.statusText || 'Unauthorized')
  error.response = res
  return error
}

export const baseRequest = async (
    path,
    method,
    body,
    headers,
    params,
    acceptJson,
    skipRefresh
) => {
  // Bail immediately if we're mid-logout — no point sending, no point refreshing.
  // Reject with a 401-shaped response so the outer request() catch takes the auth-failure branch
  // (silent) instead of the "no response received" branch that toasts "check internet connection".
  if (Auth.isLoggingOut()) {
    return Promise.reject(wrapAuthResponse({ status: 401, statusText: 'Unauthorized' }))
  }

  // Pre-flight: if token is expired, refresh before sending
  if (Auth.isTokenExpired()) {
    try {
      await refreshTokenOnce()
    } catch (e) {
      Auth.logout()
      return Promise.reject(e)
    }
  }

  const defaultHeaders = {
    Authorization: Auth.getToken(), // Always reads the latest token AFTER any refresh
    'Auth-SystemKey': '1',
    'Auth-ApiKey': '!@#ROXCLAPP!@#',
    // Only send x-client-Id when a client is actually selected. Auth.selectedClient reads
    // localStorage, which returns null on a fresh session — fetch would then serialize that as
    // the literal string "null" and the backend interceptor would key on a bogus value. Since
    // /me is the endpoint that populates selectedClient in the first place, sending a bad
    // header on that call creates a first-login deadlock. Suppressing the header when null
    // lets /me complete cleanly, populate the client id, and subsequent calls carry the real
    // header via this same default block.
    ...(Auth.selectedClient ? { 'x-client-Id': Auth.selectedClient } : {}),
  }

  const reqHeaders = {
    Accept: acceptJson ? 'application/json' : '*/*',
    'Content-Type': 'application/json',
    ...headers,
    ...defaultHeaders
  }

  const fileHeaders = {
    Accept: '*/*',
    ContentType: 'multipart/form-data',
    ...defaultHeaders
  }

  const isMultiPart = (headers && (headers['Content-Type'] || headers.ContentType)) === 'multipart/form-data'

  return fetch(getBackendUrl(path, params), {
    method: method || 'GET',
    headers: isMultiPart ? fileHeaders : reqHeaders,
    body: body ? isMultiPart ? body : JSON.stringify(body) : undefined
  }).then(res =>
      validateAuthResponse(
          res,
          path,
          method,
          body,
          headers,
          params,
          acceptJson,
          skipRefresh
      )
  )
}

/**
 * This is an ajax request function that expect/prompt a download file
 * it is expecting the backend to send a 'content-disposition' with this format:
 * "attachment; filename=fileName.type"
 * will extract the fileName and defaults the prompt to that, if its not
 * available will use the fallbackFileName instead
 * @param {string} path url to the backend
 * @param {(POST|PUT|GET|DELETE)} method the method type
 * @param {object} body the body that will be sent to the backend
 * @param {string} fallbackFileName the file name for the prompt incase it
 * wasn't sent by the backend
 */
export const downloadFile = (path, method, body, fallbackFileName) => {
  let fileName = '', status;
  return baseRequest(path, method, body)
    .then(res => {
      // Read only when the server exposes the header to the browser; handles quoted and RFC 5987 (filename*=) names.
      const disposition = res.headers.get('content-disposition') || ''
      const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
      const plain = /filename="?([^";]+)"?/i.exec(disposition)
      try {
        fileName = encoded ? decodeURIComponent(encoded[1]) : plain ? plain[1].trim() : null
      } catch (e) {
        fileName = plain ? plain[1].trim() : null
      }
      return res
    })
    .then(resp => {
      status = resp.status;
      return resp.blob();
    })
    .then(blob => {
      if (!blob.size) {
        throw new Error('Unexpected error when downloading. Please retry. If the issue persists, please contact Support.')
      }
      if (status === 404 || status === 500) return Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG })

      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = fileName || fallbackFileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
    })
    .catch(async err => {
      if (err.response) {
        try {
          if (err.response.status !== 404) {
            const parsedError = await err.response?.json()
            throwException(parsedError)
          } else {
            Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG })
          }
          throw new Error('Unexpected error when downloading. Please retry. If the issue persists, please contact Support.')
        } catch (e) {
          Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG })
          throw new Error('Unexpected error when downloading. Please retry. If the issue persists, please contact Support.')
        }
      }
      // happens for timeout exceptions
      throwException([{ message: err }])
    })
}

export const getPDF = (path) => {
  let status;
  return baseRequest(path, 'GET', null)
    .then(res => {
      status = res.status;
      return res.blob();
    })
    .then(blob => {
      if (!blob.size) {
        throw new Error('Unexpected error when getting the PDF. Please retry. If the issue persists, please contact Support.')
      }
      if (status === 404) return Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG })

      return blob;
    })
    .catch(async err => {
      if (err.response) {
        try {
          if (err.response.status !== 404) {
            const parsedError = await err.response?.json()
            throwException(parsedError)
          } else {
            Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG })
          }
          throw new Error('Unexpected error when getting the PDF. Please retry. If the issue persists, please contact Support.')
        } catch (e) {
          Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG })
          throw new Error('Unexpected error when getting the PDF. Please retry. If the issue persists, please contact Support.')
        }
      }
      // happens for timeout exceptions
      throwException([{ message: err }])
    })
}

export const getImage = (path) => {
  let status;
  return baseRequest(path, 'GET', null)
    .then(res => {
      status = res.status;
      return res.blob();
    })
    .then(blob => {
      if (!blob.size) {
        throw new Error('Unexpected error when getting the image. Please retry. If the issue persists, please contact Support.');
      }
      if (status === 404) return Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG });

      return blob;
    })
    .catch(async err => {
      if (err.response) {
        try {
          if (err.response.status !== 404) {
            const parsedError = await err.response?.json();
            throwException(parsedError);
          } else {
            Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG });
          }
          throw new Error('Unexpected error when getting the image. Please retry. If the issue persists, please contact Support.');
        } catch (e) {
          Toast({ type: 'error', message: strings.SOMETHING_WENT_WRONG });
          throw new Error('Unexpected error when getting the image. Please retry. If the issue persists, please contact Support.');
        }
      }
      // happens for timeout exceptions
      throwException([{ message: err }]);
    });
};
